import { buildAbsoluteLocalizedUrl, normalizeLocalizedHref, normalizePathWithBase } from './url';

export type AlternateLink = {
  hrefLang?: string;
  slug?: string;
};

export type LocalizationItem = {
  locale?: string | null;
  slug?: string | null;
};

export type LanguageSwitchConfig = {
  currentLocale?: string;
  baseAlternate?: string;
  alternate?: AlternateLink;
  localizations?: Array<LocalizationItem | null> | null;
  query?: Record<string, boolean | number | string | null | undefined>;
  routePrefix?: string;
};

export type LanguageSwitchData = Pick<
  LanguageSwitchConfig,
  'localizations' | 'query' | 'routePrefix'
>;

const normalizeLocale = (locale?: string | null) => {
  if (!locale) {
    return 'en';
  }

  return locale.toLowerCase().startsWith('hr') ? 'hr' : 'en';
};

const localeToLabel = (locale?: string | null) => normalizeLocale(locale).toUpperCase();

const appendQuery = (
  path: string,
  query?: Record<string, boolean | number | string | null | undefined>,
) => {
  if (!query) {
    return path;
  }

  const searchParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined || value === false) {
      return;
    }

    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  if (!queryString) {
    return path;
  }

  return `${path}${path.includes('?') ? '&' : '?'}${queryString}`;
};

const buildLocalizationPath = (
  slug?: string | null,
  routePrefix?: string,
  targetLocale?: string | null,
) => {
  if (!slug) {
    return '';
  }

  const normalizedSlug = normalizeLocalizedHref(slug, targetLocale, {
    ensureLocalePrefix: false,
  });
  const pathWithRoutePrefix = normalizePathWithBase(normalizedSlug, routePrefix || '');

  return normalizeLocalizedHref(pathWithRoutePrefix, targetLocale);
};

export const getLanguageSwitchLink = ({
  currentLocale,
  baseAlternate = '',
  alternate,
  localizations,
  query,
  routePrefix,
}: LanguageSwitchConfig) => {
  const normalizedCurrentLocale = normalizeLocale(currentLocale);
  const targetLocale = normalizedCurrentLocale === 'en' ? 'hr' : 'en';

  const localization =
    localizations?.find(item => normalizeLocale(item?.locale) === targetLocale) ||
    localizations?.find(item => normalizeLocale(item?.locale) !== normalizedCurrentLocale) ||
    null;

  if (localization?.slug && baseAlternate) {
    const path = appendQuery(
      buildLocalizationPath(localization.slug, routePrefix, localization.locale),
      query,
    );
    return {
      href: buildAbsoluteLocalizedUrl(baseAlternate, path, localization.locale),
      label: localeToLabel(localization.locale),
    };
  }

  if (alternate?.slug && baseAlternate) {
    return {
      href: buildAbsoluteLocalizedUrl(baseAlternate, alternate.slug, alternate.hrefLang),
      label: localeToLabel(alternate.hrefLang),
    };
  }

  return null;
};

const localeToHrefLang = (locale?: string | null) =>
  normalizeLocale(locale) === 'hr' ? 'hr-HR' : 'en-US';

export const getAlternateLinks = ({
  canonicalHref,
  currentLocale,
  baseAlternate = '',
  alternate,
  localizations,
  query,
  routePrefix,
}: LanguageSwitchConfig & { canonicalHref: string }) => {
  const normalizedCurrentLocale = normalizeLocale(currentLocale);
  const currentHrefLang = localeToHrefLang(normalizedCurrentLocale);
  const alternateLink = getLanguageSwitchLink({
    currentLocale,
    baseAlternate,
    alternate,
    localizations,
    query,
    routePrefix,
  });

  const links = [
    {
      href: canonicalHref,
      hrefLang: currentHrefLang,
    },
  ];

  if (alternateLink?.href) {
    links.push({
      href: alternateLink.href,
      hrefLang: localeToHrefLang(alternateLink.label),
    });
  }

  const xDefaultHref =
    normalizedCurrentLocale === 'en' ? canonicalHref : alternateLink?.href || canonicalHref;

  links.push({
    href: xDefaultHref,
    hrefLang: 'x-default',
  });

  return links.filter(
    (link, index, arr) =>
      !!link.href && arr.findIndex(item => item.hrefLang === link.hrefLang) === index,
  );
};
